import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import JSZip from 'jszip';
import { Renderer } from 'pixi.js';
import { RecordRTCPromisesHandler } from 'recordrtc';
import { BehaviorSubject, ReplaySubject, takeUntil } from 'rxjs';

import { coreSelectors } from 'app/core/state';
import { prettyPlayerId } from 'app/core/utilities/user-util';
import { AppState } from 'app/types';
import { Loggable } from 'app/utils/logging/loggable';
import { DUMMY_VIDEO_STREAM } from 'app/utils/media/stream';
import {
  IncomingMessage,
  IncomingMessageType,
  OutgoingMessageType,
  StateDumpMessage,
  StateDumpRequestMessage,
} from 'messages/websocket.messages';

import { PeerStats } from '../preciate-stream/types';
import { selectors, UserSubscriptionsState } from '../state/app';

import { DeviceService } from './device/device.service';
import { SceneService } from './scene.service';
import { ParticipantMediaStreams, VideoRoomService } from './video-room.service';
import { WebsocketService } from './websocket.service';

interface FormattedStats {
  userId: string;
  stats: PeerStats;
}

interface PrettyPlayer {
  user: string;
  userId: string;
  x: number;
  y: number;
}

interface Recording {
  blob: Blob;
  fileName: string;
  type: string;
}

const RecordingOptions = {
  type: 'video',
  mimeType: 'video/webm;codecs=vp9',
};

@Injectable({
  providedIn: 'root',
})
export class DebuggerService extends Loggable {
  private socialId = '';
  private userId?: string;
  private cameraStream: MediaStream = DUMMY_VIDEO_STREAM;
  private participantVideoStreams: ParticipantMediaStreams = {};
  private participantAudioStreams: ParticipantMediaStreams = {};

  private appState: AppState = {};
  private subscriptions: UserSubscriptionsState = {};
  private destroyed$ = new ReplaySubject<boolean>(1);

  public disableCaptureMedia = new BehaviorSubject<boolean>(false);

  public constructor(
    private store: Store<AppState>,
    private sceneService: SceneService,
    private websocketService: WebsocketService,
    private videoRoomService: VideoRoomService,
    private deviceService: DeviceService,
  ) {
    super();
    this.setDisplayName('DebuggerService');
    this.store.select((state) => state).subscribe((state) => (this.appState = state));
    this.store
      .select(selectors.getSubscriptionStatus)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((subscriptions) => {
        this.subscriptions = subscriptions ?? {};
      });

    this.store
      .select(selectors.getJoinedSocialId)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((socialId) => {
        this.socialId = socialId ?? '';
      });

    this.store
      .select(coreSelectors.getUserId)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((userId) => (this.userId = userId));

    // Main Player Media
    this.deviceService.cameraStream$.pipe(takeUntil(this.destroyed$)).subscribe((stream) => {
      this.cameraStream = stream ?? DUMMY_VIDEO_STREAM;
    });

    this.videoRoomService.participantVideoStream$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((streams: ParticipantMediaStreams) => {
        this.participantVideoStreams = streams;
      });

    this.videoRoomService.participantAudioStream$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((streams: ParticipantMediaStreams) => {
        this.participantAudioStreams = streams;
      });

    this.websocketService.message$.subscribe((message) => this.handleMessage(message));
  }

  public async captureDebugData(): Promise<void> {
    await this.onDumpState();
    await this.captureMediaRecording();
  }

  public async onDumpState(): Promise<void> {
    const dump = await this.createDump();
    const dumpLink = document.createElement('a');
    dumpLink.href = window.URL.createObjectURL(new Blob([JSON.stringify(dump, null, 2)], { type: 'text/plain' }));
    dumpLink.download = `${this.userId}_state_dump.json`;
    dumpLink.click();
  }

  private createDump(): Promise<Record<string, unknown>> {
    this.warn('Dumping state');
    return new Promise(async (resolve) => {
      const groups = this.sceneService.getGroups() ?? [];
      const worldDimensions = this.sceneService.worldDimensions;
      const mainPlayer = this.sceneService.getPlayer();
      const playerObj = mainPlayer
        ? {
            user: prettyPlayerId(mainPlayer.user),
            x: mainPlayer.x,
            y: mainPlayer.y,
          }
        : {};

      const participants = this.getParticipants();

      const userId = mainPlayer?.user?.userId ?? '';
      const playerPeerConnectionStats = await this.getMainPlayerPeerConnectionStats(userId);
      let participantPeerConnectionStats;
      if (participants.length) {
        participantPeerConnectionStats = await this.getParticipantPeerConnectionStats(participants);
      }

      const data = {
        userId: this.userId,
        ngrx: this.appState,
        groups,
        worldDimensions,
        player: playerObj,
        participants,
        playerPeerConnectionStats,
        participantPeerConnectionStats,
        subscriptions: this.subscriptions,
        websocketMessage: this.websocketService.messageHistory,
      };
      resolve(data);
    });
  }

  private getMainPlayerPeerConnectionStats(userId: string): Promise<FormattedStats> {
    let peerStats: FormattedStats;
    return new Promise(async (resolve) => {
      const stats = await this.videoRoomService.getSnapshotOfPeerConnectionStats();
      if (stats) {
        peerStats = { userId, stats };
      }
      resolve(peerStats);
    });
  }

  private getParticipantPeerConnectionStats(participants: PrettyPlayer[]): Promise<FormattedStats[]> {
    const peerStats: FormattedStats[] = [];
    return new Promise((resolve) => {
      if (!participants) {
        resolve([]);
      }
      participants.forEach(async (player) => {
        const userId = player.userId ?? '';
        const stats = await this.videoRoomService.getSnapshotOfPeerConnectionStats(userId);
        if (stats) {
          peerStats.push({ userId, stats });
        }
        resolve(peerStats);
      });
    });
  }

  private getParticipants(): PrettyPlayer[] {
    const participants: PrettyPlayer[] =
      this.sceneService.getAllParticipants().map(({ user, userId, x, y }) => ({
        user: prettyPlayerId(user),
        userId,
        x,
        y,
      })) ?? [];
    return participants;
  }

  private handleMessage(msg: IncomingMessage): void {
    const type = msg.t;
    switch (type) {
      case IncomingMessageType.STATE_DUMP_REQUEST:
        this.handleDumpRequest(msg);
    }
  }

  private handleDumpRequest(msg: StateDumpRequestMessage): void {
    const dump = this.createDump();
    const response: StateDumpMessage = {
      t: OutgoingMessageType.STATE_DUMP,
      namePostfix: msg.namePostfix,
      stateData: JSON.stringify(dump),
    };
    this.websocketService.sendMessage(response);
  }

  public async captureMediaRecording(): Promise<void> {
    this.disableCaptureMedia.next(true);
    const recordings: Recording[] = [];

    const { sceneManager } = this.sceneService;
    if (!sceneManager) {
      return;
    }
    // Canvas Media
    const { renderer } = sceneManager.app as unknown as { renderer: Renderer };
    const r = renderer as Renderer;
    const canvas = r.view;
    const canvasStream = (canvas as unknown as HTMLCanvasElement).captureStream() as MediaStream;
    const canvasRecording = await this.createRecording(canvasStream);
    recordings.push({ blob: canvasRecording, fileName: `canvas`, type: 'canvas' });

    // Main Player Media
    const mainPlayerRecording = await this.createRecording(this.cameraStream);
    const fileName = this.userId ?? 'main-player';
    recordings.push({ blob: mainPlayerRecording, fileName, type: 'video' });

    // Participant Media
    const participantsLength = Object.keys(this.participantVideoStreams).length;

    if (!participantsLength) {
      await this.downloadZip(recordings);
      return;
    }

    Object.entries(this.participantVideoStreams).forEach(async ([key, value], index) => {
      const audioTrack = this.participantAudioStreams[key].stream?.getAudioTracks()[0];
      const videoTrack = value.stream?.getVideoTracks()[0];

      const stream = new MediaStream();

      if (videoTrack) {
        stream.addTrack(videoTrack);
      }

      if (audioTrack) {
        stream.addTrack(audioTrack);
      }

      const recording = await this.createRecording(stream);
      recordings.push({ blob: recording, fileName: `${key}`, type: 'video' });

      if (index === participantsLength - 1) {
        await this.downloadZip(recordings);
        return;
      }
    });
  }

  private async createRecording(stream: MediaStream): Promise<Blob> {
    let blob = new Blob();
    try {
      // @ts-expect-error 'new' expression, whose target lacks a construct signature, implicitly has an 'any' type.
      const recorder = new RecordRTCPromisesHandler(stream, RecordingOptions);
      await recorder.startRecording();

      const sleep = (m: number | undefined) => new Promise((r) => setTimeout(r, m));
      await sleep(15000);
      await recorder.stopRecording();
      blob = await recorder.getBlob();
    } catch (e) {
      this.error('Unable to create Recording', e);
    }
    return blob;
  }

  private async downloadZip(recordings: Recording[]): Promise<void> {
    const zip = new JSZip();
    recordings.forEach((video) => {
      zip.file(`${video.fileName}.webm`, video.blob);
    });
    let url: string | undefined;

    try {
      const content = await zip.generateAsync({ type: 'blob' });

      url = URL.createObjectURL(content);
      const a = document.createElement('a');
      a.href = url;
      a.target = '_blank';
      a.download = `${this.socialId}-player-media`;
      a.click();
    } catch (e) {
      this.error('Error while downloading the media recordings', e);
      this.disableCaptureMedia.next(false);
    } finally {
      if (url) {
        URL.revokeObjectURL(url);
      }
      this.disableCaptureMedia.next(false);
    }
  }
}
